import React from 'react';
import UnifiedSimpleConversionExplanation from './UnifiedSimpleConversionExplanation';

const FrequencyConversionExplanation = ({
  fromUnit,
  toUnit,
  inputValue,
  result,
  darkMode
}) => {
  const getConversionData = () => {
    // Hertz to other units
    if (fromUnit === 'Hertz' && toUnit === 'Kilohertz') {
      return {
        formula: 'kHz = Hz ÷ 1000',
        conversionSteps: [
          `Take the frequency in hertz: ${inputValue}Hz`,
          `Divide by 1000: ${inputValue} ÷ 1000 = ${result}kHz`
        ],
        realWorldExample: "FM radio stations broadcast between 87.5 and 108.0 megahertz (MHz).",
        funFacts: [
          "The hertz is named after Heinrich Hertz, who proved the existence of electromagnetic waves.",
          "Human hearing ranges from about 20 Hz to 20 kHz.",
          "A musical note 'A' above middle C is defined as exactly 440 Hz.",
        ]
      };
    }

    if (fromUnit === 'Hertz' && toUnit === 'Revolutions per minute (RPM)') {
      return {
        formula: 'RPM = Hz × 60',
        conversionSteps: [
          `Take the frequency in hertz: ${inputValue}Hz`,
          `Multiply by 60 (seconds in a minute): ${inputValue} × 60 = ${result}RPM`
        ],
        realWorldExample: "A typical computer hard drive spins at 5400 or 7200 RPM.",
        funFacts: [
          "Car engines typically idle at around 800-1000 RPM.",
          "Old vinyl records play at 33⅓ or 45 RPM.",
          "Some high-speed dental drills can reach 400,000 RPM.",
        ]
      };
    }

    if (fromUnit === 'Megahertz' && toUnit === 'Gigahertz') {
      return {
        formula: 'GHz = MHz ÷ 1000',
        conversionSteps: [
          `Take the frequency in megahertz: ${inputValue}MHz`,
          `Divide by 1000: ${inputValue} ÷ 1000 = ${result}GHz`
        ],
        realWorldExample: "Modern CPU clock speeds are typically between 2-5 GHz.",
        funFacts: [
          "Wi-Fi networks operate at either 2.4 GHz or 5 GHz.",
          "The first personal computers ran at less than 5 MHz.",
          "Microwave ovens typically operate at 2.45 GHz.",
        ]
      };
    }

    if (fromUnit === 'Hertz' && toUnit === 'Beats per minute (BPM)') {
      return {
        formula: 'BPM = Hz × 60',
        conversionSteps: [
          `Take the frequency in hertz: ${inputValue}Hz`,
          `Multiply by 60 (seconds in a minute): ${inputValue} × 60 = ${result}BPM`
        ],
        realWorldExample: "A typical resting heart rate is 60-100 BPM (1-1.67 Hz).",
        funFacts: [
          "Dance music typically ranges from 120-140 BPM.",
          "The world record for fastest heart rate is about 480 BPM.",
          "Classical music often varies between 60-80 BPM.",
        ]
      };
    }

    if (fromUnit === 'Hertz' && toUnit === 'Wavelength (meters)') {
      return {
        formula: 'λ = c ÷ f, where c is speed of light (299,792,458 m/s)',
        conversionSteps: [
          `Take the frequency in hertz: ${inputValue}Hz`,
          `Divide speed of light by frequency: 299,792,458 ÷ ${inputValue} = ${result}m`
        ],
        realWorldExample: "Visible light has wavelengths between 380-700 nanometers.",
        funFacts: [
          "Radio waves can have wavelengths from meters to kilometers.",
          "X-rays have wavelengths around 0.01-10 nanometers.",
          "The relationship between frequency and wavelength is inversely proportional.",
        ]
      };
    }

    if (fromUnit === 'Hertz' && toUnit === 'Radians per second') {
      return {
        formula: 'rad/s = Hz × 2π',
        conversionSteps: [
          `Take the frequency in hertz: ${inputValue}Hz`,
          `Multiply by 2π: ${inputValue} × 2π = ${result}rad/s`
        ],
        realWorldExample: "A motor spinning at 3600 RPM rotates at about 377 radians per second.",
        funFacts: [
          "One complete rotation equals 2π radians.",
          "Angular velocity in radians/second is often used in physics calculations.",
          "The Earth rotates at approximately 0.00007272 radians per second.",
        ]
      };
    }

    // Default case for other conversions
    return {
      formula: `Converting from ${fromUnit} to ${toUnit}`,
      conversionSteps: [
        `Start with ${inputValue} ${fromUnit}`,
        `Apply conversion factor`,
        `Result: ${result} ${toUnit}`
      ],
      realWorldExample: "Frequency conversions are essential in electronics, music, and telecommunications.",
      funFacts: [
        "Frequency is the number of occurrences of a repeating event per unit of time.",
        "The SI unit of frequency is the hertz (Hz).",
        "Different frequency ranges are used for different types of electromagnetic radiation.",
      ]
    };
  };

  const conversionData = getConversionData();

  return (
    <UnifiedSimpleConversionExplanation
      conversionType="frequency"
      fromUnit={fromUnit}
      toUnit={toUnit}
      inputValue={inputValue}
      result={result}
      darkMode={darkMode}
      {...conversionData}
    />
  );
};

export default FrequencyConversionExplanation; 